import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { OrthomolIcon } from '~/components-common/OrthomolIcon';
import { OrdersList } from './OrdersList';
import { OrdersDebugBox } from './OrdersDebugBox';
import { OrdersProvider, useOrders } from './OrdersContext';

const OrdersPageContent = () => {
  const {
    orders,
    dateRangeOptions,
    isLoading,
    hasError,
    currentPage,
    totalPages,
    totalCount,
    sortOrder,
    dateRange,
    hasActiveFilters,
    handleSortChange,
    handleDateRangeChange,
    handleResetFilters,
    handlePageChange,
  } = useOrders();

  const [expandedOrder, setExpandedOrder] = useState<string | null>(null); // OrderDetails expanded
  const [allFiltersExpanded, setAllFiltersExpanded] = useState<boolean>(false); // Filters UI Expanded

  // Listen to Bootstrap collapse events to sync state
  useEffect(() => {
    const collapseElement = document.getElementById('orders-all-filters');
    if (collapseElement) {
      const handleShow = () => setAllFiltersExpanded(true);
      const handleHide = () => setAllFiltersExpanded(false);

      collapseElement.addEventListener('shown.bs.collapse', handleShow);
      collapseElement.addEventListener('hidden.bs.collapse', handleHide);

      // Initialize state based on current class
      if (collapseElement.classList.contains('show')) {
        setAllFiltersExpanded(true);
      }

      return () => {
        collapseElement.removeEventListener('shown.bs.collapse', handleShow);
        collapseElement.removeEventListener('hidden.bs.collapse', handleHide);
      };
    }
  }, []);

  // Sync allFiltersExpanded when filters become active
  useEffect(() => {
    if (hasActiveFilters && !allFiltersExpanded) {
      setAllFiltersExpanded(true);
    }
  }, [hasActiveFilters]);

  // Wrapper for handleDateRangeChange to also expand filters
  const handleDateRangeChangeWithExpand = (range: string) => {
    handleDateRangeChange(range);
    if (range) {
      setAllFiltersExpanded(true);
    }
  };

  // Wrapper for handleResetFilters to also collapse filters
  const handleResetFiltersWithCollapse = () => {
    handleResetFilters();
    setAllFiltersExpanded(false);
  };

  // UI handler for expanding/collapsing order details
  const toggleOrderDetails = (orderNumber: string) => {
    setExpandedOrder(expandedOrder === orderNumber ? null : orderNumber);
  };

  return (
    <>
      {typeof window !== 'undefined' && window.localStorage?.getItem('eshop_debug') === '1' && <OrdersDebugBox />}

      {/* Sort and Filter Controls */}
      <div className="d-flex flex-column flex-md-row justify-content-md-between">
        <span className="list-component-categories d-flex gap-2 mx-n4 mx-md-0 mb-4 mb-md-0 overflow-auto">
          <label className={sortOrder === 'newest' ? 'active' : ''} role="button">
            <input
              type="radio"
              name="orders-sort"
              value="newest"
              checked={sortOrder === 'newest'}
              onClick={() => handleSortChange('newest')}
              onChange={() => {}}
              hidden
            />
            Neueste zuerst
          </label>
          <label className={sortOrder === 'oldest' ? 'active' : ''} role="button">
            <input
              type="radio"
              name="orders-sort"
              value="oldest"
              checked={sortOrder === 'oldest'}
              onClick={() => handleSortChange('oldest')}
              onChange={() => {}}
              hidden
            />
            Älteste zuerst
          </label>
        </span>
        <button
          id="orders-all-filters-button"
          className={`btn justify-content-center ${allFiltersExpanded ? 'btn-primary' : 'btn-ghost'} px-lg-6`}
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#orders-all-filters"
          aria-expanded={allFiltersExpanded ? 'true' : 'false'}
          aria-controls="orders-all-filters"
        >
          <span>Alle Filter</span>
          <span className="icon">
            <OrthomolIcon name="filter-alternative-24" size="16" />
          </span>
        </button>
      </div>

      {/* Filter Dropdowns */}
      <div id="orders-all-filters" className={`collapse ${allFiltersExpanded ? 'show' : ''}`}>
        <div className="dropdown-holder d-flex gap-2 gap-md-3 mx-n4 mx-md-0 mb-4 pt-4">
          {/* Date Range Filter */}
          <div className="dropdown ms-4 ms-md-0 me-4 me-md-0">
            <button
              id="orders-date-range-button"
              className="btn btn-ghost"
              type="button"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              data-bs-auto-close="outside"
              aria-expanded="false"
            >
              <span className="d-flex gap-1 align-items-center">
                Zeitraum
                <span id="orders-date-range-badge" className="badge bg-primary rounded-circle p-0" hidden={!dateRange}>
                  1
                </span>
              </span>
              <span className="icon">
                <OrthomolIcon name="chevron-24" size="16" rotation={90} />
              </span>
            </button>
            <div
              className="dropdown-menu bg-light border-0 shadow mt-2"
              style={{ minWidth: '200px' }}
              aria-labelledby="orders-date-range-button"
            >
              <ul className="mb-0">
                {dateRangeOptions.map((option, index) => (
                  <li key={option.value} className={index < dateRangeOptions.length - 1 ? 'mb-2' : ''}>
                    <label className="d-flex gap-2 align-items-baseline text-nowrap">
                      <input
                        className="orders-date-range"
                        type="radio"
                        name="orders-date-range"
                        value={option.value}
                        checked={dateRange === option.value}
                        onChange={() => handleDateRangeChangeWithExpand(option.value)}
                      />
                      {option.label}
                    </label>
                  </li>
                ))}
              </ul>
              {/* "Auswahl speichern" button removed - filters are applied directly */}
              {/* <button
                id="orders-date-range-close-button"
                className="btn btn-primary btn-block mt-2"
                type="button"
                onClick={() => {
                  const button = document.getElementById('orders-date-range-button');
                  if (button) {
                    (button as HTMLElement).click();
                  }
                }}
              >
                <span>Auswahl speichern</span>
                <span className="icon m-0">
                  <OrthomolIcon name="arrow-24" size="24" />
                </span>
              </button> */}
            </div>
          </div>
        </div>
        <div
          id="orders-reset-button"
          className={`text-orthomol-blue d-inline-flex align-items-center ${!hasActiveFilters ? 'd-none' : ''}`}
          role="button"
          onClick={handleResetFiltersWithCollapse}
        >
          <span className="icons ms-0 me-2">
            <OrthomolIcon name="cross-24" size="16" />
          </span>
          <span className="pt-1">Alle Filter zurücksetzen</span>
        </div>
      </div>

      {/* Orders List */}
      {isLoading ? (
        <div className="text-center my-7">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Bestellungen werden geladen...</span>
          </div>
        </div>
      ) : hasError ? (
        <div className="text-center my-7">
          <p className="m-0">Fehler beim Laden der Bestellungen</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center my-7">
          <p className="m-0">
            {hasActiveFilters
              ? 'Mit den aktuellen Filtern wurden keine Bestellungen gefunden.'
              : 'Keine Bestellungen gefunden.'}
          </p>
        </div>
      ) : (
        <>
          <OrdersList orders={orders} expandedOrder={expandedOrder} onToggleDetails={toggleOrderDetails} />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 my-5">
              <button
                className="btn btn-ghost"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Vorherige Seite"
              >
                &lt;
              </button>

              {/* Page numbers */}
              <div className="d-flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first, last, current, and adjacent pages
                  const showPage =
                    page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);

                  // Show ellipsis
                  const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                  const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

                  if (showEllipsisBefore || showEllipsisAfter) {
                    return (
                      <span key={page} className="px-2">
                        ...
                      </span>
                    );
                  }

                  if (!showPage) return null;

                  return (
                    <button
                      key={page}
                      className={`btn ${page === currentPage ? 'btn-primary' : 'btn-ghost'} px-3`}
                      onClick={() => handlePageChange(page)}
                      aria-label={`Seite ${page}`}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                className="btn btn-ghost"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Nächste Seite"
              >
                &gt;
              </button>
            </div>
          )}

          {/* Pagination info */}
          <div className="text-center text-muted small mt-4 mb-4">
            Seite {currentPage} von {totalPages} ({totalCount} Bestellungen gesamt)
          </div>
        </>
      )}
    </>
  );
};

export const OrdersPage = () => {
  return (
    <OrdersProvider>
      <OrdersPageContent />
    </OrdersProvider>
  );
};
